import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { routes } from '@platform/core/lib/routes';
import type { IRecord } from '@platform/core/types';
import { BriefingMessages } from './BriefingMessages';
import { BriefingObjections } from './BriefingObjections';
import { BriefingOpponents } from './BriefingOpponents';
import { BriefingTasks } from './BriefingTasks';
import type { IVizProps } from './IVizProps';
import { useVizLabels } from './useVizLabels';
import { useVizRecords } from './useVizRecords';

/**
 * Брифінг плану інтерв'ю (прототип v4 vizIntv): картка з акцентною смугою — «видання · журналіст · формат · ефір · дата» (дата як у
 * прототипі — рррр-мм-дд), тема 17px,
 * мета й підсумок «Тез: N · заперечень: N (відповідь готова: N) · опонентів: N · завдань виконано: N/M»; далі дві сітки по дві картки:
 * «Що казати» · «Заперечення та наші відповіді», «Опоненти» · «Підготовчі завдання». Клік по елементу відкриває його запис.
 */
export function BriefingViz({ ws, module, rt, record }: IVizProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const own = useVizLabels(rt);
  const msgs = useVizRecords(ws, module, rt.key, record, 'mkind');
  const objs = useVizRecords(ws, module, rt.key, record, 'answer');
  const opps = useVizRecords(ws, module, rt.key, record, 'claims');
  const tasks = useVizRecords(ws, module, rt.key, record, 'ptype');
  const labels = { msgs: useVizLabels(msgs.source?.rt), objs: useVizLabels(objs.source?.rt), opps: useVizLabels(opps.source?.rt), tasks: useVizLabels(tasks.source?.rt) };
  const opener = (key: string | undefined) => (r: IRecord) => { if (key) navigate(routes.record(module.module.key, key, r.id)); };
  const v = record.values;
  const head = [v['outlet'], v['journalist'], own.option('format', v['format']), own.option('live', v['live']), v['date']].filter(Boolean).join(' · ');
  const stats = t('recordPage.viz.briefing.stats', {
    msgs: msgs.items.length, objs: objs.items.length, ready: objs.items.filter((r) => r.values['answer']).length,
    opps: opps.items.length, done: tasks.items.filter((r) => r.isClosed).length, tasks: tasks.items.length,
  });
  return (
    <div className="viz viz-brief">
      <section className="viz-brief__head">
        {head && <span className="viz-brief__meta">{head}</span>}
        <strong className="viz-brief__topic">{String(v['topic'] || record.title)}</strong>
        {v['goal'] ? <span className="viz-brief__goal">{String(v['goal'])}</span> : null}
        <span className="viz-brief__stats">{stats}</span>
      </section>
      <div className="viz-brief__grid">
        <BriefingMessages items={msgs.items} labels={labels.msgs} onOpen={opener(msgs.source?.rt.key)} />
        <BriefingObjections items={objs.items} labels={labels.objs} onOpen={opener(objs.source?.rt.key)} />
      </div>
      <div className="viz-brief__grid">
        <BriefingOpponents items={opps.items} labels={labels.opps} onOpen={opener(opps.source?.rt.key)} />
        <BriefingTasks items={tasks.items} labels={labels.tasks} onOpen={opener(tasks.source?.rt.key)} />
      </div>
    </div>
  );
}
